/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The reservation "add items" picker — the create-order service-subject catalog
 * (inspection templates / construction categories …) reused via the
 * ServiceSubjectsHost seam. It is seeded
 * with the reservation's current items and backed by a standalone host (no order
 * flow); the catalog's +/- edit the set in place, and on leaving the full set is
 * returned to the detail screen via `onPicked`. Inspection items carry their
 * templateId but no assignee (assigned later, at order creation).
 */

import { MemberPickerBottomSheetProvider } from '@euler/app/components/MemberPickerBottomSheet';
import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { Typography } from '@euler/app/components/typography';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { ServiceSubjectsCatalog } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/components/ServiceSubjectsCatalog';
import {
  ServiceSubjectsHost,
  ServiceSubjectsHostProvider,
} from '@euler/app/flows/order/pages/ServiceSubjectsScreen/host';
import { OrderExtra } from '@euler/app/flows/order/types';
import {
  isInspectionTemplateNominatedFor,
  useInspectionTemplates,
} from '@euler/app/flows/task/functions/useInspectionTemplates';
import { Center } from '@euler/components/Center';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ServiceSubject } from '@euler/model/OrderDetail';
import { InspectionCategory } from '@euler/model/enum';
import { ReservationItem } from '@euler/model/reservation/ReservationItem';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import produce from 'immer';
import { Plus, Search } from 'lucide-react-native';
import { ReactNode, Suspense, memo, useEffect } from 'react';
import { TextInput, TouchableOpacity, View } from 'react-native';
import { BehaviorSubject } from 'rxjs';

type PickerParams = {
  initialItems?: ReservationItem[];
  vehicle?: { licensePlateNo?: string; vin?: string };
  onPicked: (items: ReservationItem[]) => void;
};

export const ReservationItemPickerScreen = wrapNavigatorScreen(
  ({ initialItems, vehicle, onPicked }: PickerParams) => {
    const { rpx } = useResponsiveContext();

    // The picked-items container + the catalog's shared subjects. Created once
    // and shared between the top bar (search / custom-add) and the catalog body.
    const detail$ = useCreation(
      () =>
        new BehaviorSubject<OrderExtra>({
          serviceSubjects: (initialItems ?? []) as ServiceSubject[],
        }),
      // Seed once from the nav param; later edits live in the subject.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );
    const showCustom$ = useCreation(
      () => new BehaviorSubject<boolean | ServiceSubject>(false),
      [],
    );
    const query$ = useCreation(() => new BehaviorSubject<string>(''), []);

    // Persist the final set when the screen unmounts (back / hardware back),
    // only when it actually changed from the seed.
    const seedKey = useCreation(
      () => JSON.stringify(initialItems ?? []),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );
    useEffect(() => {
      return () => {
        const next = detail$.value.serviceSubjects ?? [];
        if (JSON.stringify(next) !== seedKey) {
          onPicked(next as ReservationItem[]);
        }
      };
      // Run only on unmount; the subjects + callback are stable.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <MemberPickerBottomSheetProvider>
        <View style={{ flex: 1, backgroundColor: LD.canvas }}>
          {/* Drives the nav title + cancel button off the search state. */}
          <SearchHeaderController query$={query$} />
          {/* Search + custom-add bar beneath the nav header. */}
          <View
            style={{
              paddingTop: rpx(12),
              paddingBottom: rpx(10),
              paddingHorizontal: rpx(12),
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(8),
              backgroundColor: LD.surface,
              borderBottomWidth: 1,
              borderBottomColor: LD.hairline,
            }}
          >
            <SearchField query$={query$} />
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={() => showCustom$.next(true)}
              hitSlop={{ top: rpx(8), bottom: rpx(8), left: rpx(8) }}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingHorizontal: rpx(10),
                height: rpx(34),
                borderRadius: rpx(999),
                backgroundColor: LD.brandSoft,
              }}
            >
              <Plus size={rpx(15)} color={LD.brand} />
              <Typography.Text
                size={rpx(13)}
                color={LD.brand}
                bold
                style={{ marginLeft: rpx(2) }}
              >
                自定义
              </Typography.Text>
            </TouchableOpacity>
          </View>

          <Suspense
            fallback={
              <Center css={'flex: 1;'}>
                <DefaultLoader />
              </Center>
            }
          >
            <CatalogHost
              detail$={detail$}
              showCustom$={showCustom$}
              query$={query$}
              vehicle={vehicle ?? {}}
            >
              <ServiceSubjectsCatalog />
            </CatalogHost>
          </Suspense>
        </View>
      </MemberPickerBottomSheetProvider>
    );
  },
  {
    title: '添加预约项目',
  },
);

// Renders nothing; while the search box has text it retitles the screen and
// adds a "取消" header button that clears the search. Isolated from the heavy
// catalog subtree so each keystroke only re-renders this null component.
const SearchHeaderController = memo(
  ({ query$ }: { query$: BehaviorSubject<string> }) => {
    const { rpx } = useResponsiveContext();
    const navigation = useNavigation();
    const [query, setQuery] = useBehaviorSubject(query$);
    const searching = query.trim().length > 0;
    useEffect(() => {
      navigation.setOptions({
        title: searching ? '搜索预约项目' : '添加预约项目',
        headerRight: searching
          ? () => (
              <TouchableOpacity
                activeOpacity={0.7}
                onPress={() => setQuery('')}
                hitSlop={{ top: rpx(8), bottom: rpx(8), left: rpx(8) }}
                style={{ marginRight: rpx(14) }}
              >
                <Typography.Text size={rpx(15)} color={LD.brand} medium>
                  取消
                </Typography.Text>
              </TouchableOpacity>
            )
          : undefined,
      });
    }, [navigation, searching, setQuery, rpx]);
    return null;
  },
);

const SearchField = memo(({ query$ }: { query$: BehaviorSubject<string> }) => {
  const { rpx } = useResponsiveContext();
  const [query, setQuery] = useBehaviorSubject(query$);
  return (
    <View
      style={{
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: rpx(12),
        height: rpx(40),
        borderRadius: rpx(12),
        backgroundColor: LD.canvas,
      }}
    >
      <Search size={rpx(17)} color={LD.mist} />
      <TextInput
        value={query}
        onChangeText={setQuery}
        placeholder="搜索项目"
        placeholderTextColor={LD.mist}
        clearButtonMode="while-editing"
        style={{
          flex: 1,
          marginLeft: rpx(8),
          fontSize: rpx(15),
          color: LD.ink,
          padding: 0,
        }}
      />
    </View>
  );
});

// Builds the standalone host (templates load here, so this child suspends) and
// provides it to the reused catalog.
const CatalogHost = memo(
  ({
    detail$,
    showCustom$,
    query$,
    vehicle,
    children,
  }: {
    detail$: BehaviorSubject<OrderExtra>;
    showCustom$: BehaviorSubject<boolean | ServiceSubject>;
    query$: BehaviorSubject<string>;
    vehicle: { licensePlateNo?: string; vin?: string };
    children: ReactNode;
  }) => {
    const templates = useInspectionTemplates().filter(t =>
      isInspectionTemplateNominatedFor(InspectionCategory.Normal, t),
    );
    const skipJob$ = useCreation(() => new BehaviorSubject<boolean>(true), []);
    const updateDetail = usePersistFn(
      (mutator: (draft: OrderExtra) => void) => {
        const next = produce(detail$.value, mutator);
        if (next !== detail$.value) detail$.next(next);
      },
    );

    const host: ServiceSubjectsHost = {
      orderDetail$: detail$,
      updateDetail,
      inspectionTemplates: templates,
      showCustomServiceSubjectModal$: showCustom$,
      serviceSubjectQuery$: query$,
      skipJobAssignment$: skipJob$,
      jobAssignmentEnabled: false,
      capabilities: undefined,
      vehicle,
      validateServiceSubjects: undefined,
    };

    return (
      <ServiceSubjectsHostProvider value={host}>
        {children}
      </ServiceSubjectsHostProvider>
    );
  },
);
